import React, { useState } from "react";
import { Loader2 } from "lucide-react";
import AgentInstructionsEditor from "./AgentInstructionsEditor";
import AgentsSection from "./AgentsSection";
import ConnectAssistant from "./ConnectAssistant";
import ConnectedAssistants from "./ConnectedAssistants";
import { useAgentConnections } from "../../../hooks/queries/useAgentConnections";
import { hostedAssistantNames } from "../../../lib/hostedAssistants";

/**
 * Connecting JuDI to an assistant a person already talks to.
 *
 * A connection is personal, not a developer credential: one person, one
 * assistant, on or off. That is why there is a name and a choice of what it
 * may do, and nothing else — no expiry to forget about, no usage plan.
 *
 * The tab opens on what matters now. With nothing connected, the steps to
 * connect are open. Once something is connected, the list leads and the
 * steps fold away to one line.
 */

const AgentsSettings: React.FC = () => {
  const {
    data: connections = [],
    isLoading,
    isError,
    refetch,
  } = useAgentConnections();
  const hasConnections = connections.length > 0;
  // A list that did not load is not an empty one: saying nothing is connected
  // would hide the Turn off button of an assistant that is.
  const noneYet = !isLoading && !isError && !hasConnections;

  // null until the person opens or closes the part: it then follows the state.
  const [connectOpen, setConnectOpen] = useState<boolean | null>(null);
  const connectExpanded = connectOpen ?? noneYet;

  return (
    <div>
      <div className="pb-6">
        <h3 className="text-lg font-semibold text-warm-text-primary mb-1">
          Agents
        </h3>
        <p className="text-sm text-warm-text-secondary">
          Let an assistant you already use see your tasks and write down what
          you tell it about them.
        </p>
      </div>

      <div className="divide-y divide-warm-border-subtle border-t border-warm-border-subtle pt-6">
        {(isLoading || isError || hasConnections) && (
          <AgentsSection
            title="Connected assistants"
            description={
              hasConnections &&
              `${connections.length} ${
                connections.length === 1 ? "assistant can" : "assistants can"
              } reach your JuDI now.`
            }
          >
            {isLoading ? (
              <div className="flex items-center gap-2 text-sm text-warm-text-tertiary py-3">
                <Loader2 size={15} className="animate-spin" />
                Loading
              </div>
            ) : isError ? (
              <div
                role="alert"
                className="flex items-center justify-between gap-3 p-4 border border-warm-border rounded-xl"
              >
                <p className="text-sm text-warm-text-secondary">
                  Your connected assistants could not be loaded.
                </p>
                <button
                  type="button"
                  onClick={() => refetch()}
                  className="flex-shrink-0 text-xs px-2.5 py-1.5 rounded-lg border border-warm-border text-warm-text-secondary hover:bg-warm-bg-elevated transition-colors"
                >
                  Try again
                </button>
              </div>
            ) : (
              <ConnectedAssistants connections={connections} />
            )}
          </AgentsSection>
        )}

        <AgentsSection
          title={hasConnections ? "Connect another assistant" : "Connect an assistant"}
          description={
            noneYet
              ? "No assistant is connected yet."
              : `${hostedAssistantNames("and")} connect from the assistant itself.`
          }
          expanded={connectExpanded}
          onToggle={() => setConnectOpen(!connectExpanded)}
        >
          <ConnectAssistant />
        </AgentsSection>

        <AgentInstructionsEditor noAssistantYet={noneYet} />
      </div>
    </div>
  );
};

export default AgentsSettings;
